import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Switch, FormControlLabel,
  MenuItem, Alert, Typography, Stack,
  Chip, CircularProgress, FormHelperText
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import voiceFlowConstraintService from '../../services/voiceFlowConstraintService';

const CONSTRAINT_TYPES = [
  { value: 'blackout', label: '完全不接受訂位（blackout）' },
  { value: 'closed', label: '當日店休（closed）' },
  { value: 'limited_hours', label: '部分時段不開放（limited_hours）' }
];

const FALLBACK_ACTIONS = [
  { value: 'human_callback', label: '收集電話、轉真人回電' },
  { value: 'suggest_alt_date', label: '請客戶改其他日期' }
];

// 用 'none' sentinel 取代空字串 — 避免 MUI Select 在 value='' 時顯示為空
const RECURRING_NONE = 'none';
const RECURRING_OPTIONS = [
  { value: RECURRING_NONE, label: '不重複（單次）' },
  { value: 'yearly_by_date', label: '每年同日期重複' }
];
const recurringLabel = (value) =>
  (RECURRING_OPTIONS.find((o) => o.value === value) || RECURRING_OPTIONS[0]).label;

// 音檔狀態 → 中文標籤（與 FAQ 一致）
const AUDIO_STATUS_LABELS = {
  ready: '已就緒',
  stale: '需更新',
  pending: '排隊中',
  generating: '產生中',
  failed: '失敗',
  missing: '未產生'
};

const NEUTRAL_CHIP_SX = {
  bgcolor: '#f5f5f5',
  color: 'rgba(0,0,0,0.72)',
  border: '1px solid rgba(0,0,0,0.12)',
  fontWeight: 500,
  fontSize: '12px',
  height: '24px'
};

// ----------------------------------------------------------------
// 字串 ↔ Date 轉換 — 後端用 'YYYY-MM-DD'，DatePicker 吃 Date
// ----------------------------------------------------------------
const isoToDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const emptyForm = () => ({
  name: '',
  constraint_type: 'blackout',
  start_date: '',
  end_date: '',
  all_day: true,
  start_time: '',
  end_time: '',
  recurring: RECURRING_NONE,
  message_text: '',
  fallback_action: 'human_callback',
  priority: 100,
  enabled: true
});

const fromRow = (row) => ({
  name: row.name || '',
  constraint_type: row.constraintType || 'blackout',
  start_date: row.startDate || '',
  end_date: row.endDate || '',
  all_day: row.allDay !== undefined ? row.allDay : true,
  start_time: row.startTime ? row.startTime.slice(0, 5) : '',
  end_time: row.endTime ? row.endTime.slice(0, 5) : '',
  recurring: row.recurring || RECURRING_NONE,
  message_text: row.messageText || '',
  fallback_action: row.fallbackAction || 'human_callback',
  priority: row.priority !== undefined ? row.priority : 100,
  enabled: row.enabled !== undefined ? row.enabled : true
});

// 從 constraint row 抽出音檔欄位 → audioInfo state
const audioInfoFromRow = (row) => ({
  status: row?.audioStatus ?? null,
  assetKey: row?.audioAssetKey ?? null,
  duration: row?.audioDuration ?? null,
  publicUrl: row?.audioPublicUrl ?? null,
  updatedAt: row?.audioUpdatedAt ?? null,
  hasAudio: row?.hasAudio ?? false,
  error: null
});

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = '名稱必填';
  if (form.name.length > 64) errors.name = '名稱不可超過 64 字元';
  if (!form.start_date) errors.start_date = '起始日期必填';
  if (!form.end_date) errors.end_date = '結束日期必填';
  if (form.start_date && form.end_date && form.start_date > form.end_date) {
    errors.end_date = '結束日期不可早於起始日期';
  }
  if (!form.all_day) {
    if (!form.start_time) errors.start_time = '時段啟始時間必填';
    if (!form.end_time) errors.end_time = '時段結束時間必填';
    if (form.start_time && form.end_time && form.start_time >= form.end_time) {
      errors.end_time = '結束時間必須晚於起始時間';
    }
  }
  if (!form.message_text.trim()) errors.message_text = '客戶聽到的回覆語音必填';
  if (form.priority === '' || form.priority === null || isNaN(Number(form.priority)) || Number(form.priority) < 0) {
    errors.priority = '優先順序必須為非負整數';
  }
  return errors;
};

// 共用大字體樣式 — 起始/結束日期欄位
const datePickerSlotProps = (errorMsg) => ({
  textField: {
    size: 'medium',
    fullWidth: true,
    error: !!errorMsg,
    helperText: errorMsg,
    sx: {
      '& .MuiInputLabel-root': { fontSize: '1.05rem' },
      '& .MuiInputBase-input': { fontSize: '1.2rem', py: 1.25 },
    },
  },
});

const ConstraintEditDialog = ({ open, flowId, constraint, onCancel, onSubmit, onRowSaved, saving, warnings }) => {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [audioInfo, setAudioInfo] = useState(audioInfoFromRow(null));
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioError, setAudioError] = useState('');

  // Dependency uses constraint?.id (not the whole constraint object) so that a
  // parent patch with the same id (e.g. after handleRegenerateAudio's auto-save)
  // doesn't reset form / audioInfo / audioBusy mid-flow.
  useEffect(() => {
    if (open) {
      setForm(constraint ? fromRow(constraint) : emptyForm());
      setErrors({});
      setAudioInfo(audioInfoFromRow(constraint));
      setAudioError('');
      setAudioBusy(false);
    }
  }, [open, constraint?.id]);

  // Single-source-of-truth: parent's table-level socket handler (in
  // ConstraintTab) patches editDialog.constraint when the matching event
  // arrives, so a fresh constraint prop streams in here. Sync audioInfo
  // from constraint's audio fields whenever they change. No second socket
  // subscription needed (which was racing on registration timing).
  useEffect(() => {
    if (!constraint) return;
    setAudioInfo(audioInfoFromRow(constraint));
    if (constraint.audioStatus &&
        constraint.audioStatus !== 'pending' &&
        constraint.audioStatus !== 'generating') {
      setAudioBusy(false);
    }
  }, [
    constraint?.audioStatus,
    constraint?.audioAssetKey,
    constraint?.audioDuration,
    constraint?.audioPublicUrl,
    constraint?.audioUpdatedAt
  ]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Detect unsaved local edits — used to gate the regenerate button so the
  // user can't generate audio for text that isn't saved yet.
  const isFormDirty = () => {
    if (!constraint) return false;
    const original = fromRow(constraint);
    const keys = ['name', 'constraint_type', 'start_date', 'end_date', 'all_day',
      'start_time', 'end_time', 'recurring', 'message_text', 'fallback_action',
      'priority', 'enabled'];
    return keys.some((k) => String(form[k] ?? '') !== String(original[k] ?? ''));
  };

  // Build the wire-format payload (snake_case bridge for backend, sentinels
  // converted) — shared by 儲存 and 重新生成語音 (the latter saves first).
  const buildPayload = () => ({
    ...form,
    priority: Number(form.priority),
    start_time: form.all_day ? null : form.start_time,
    end_time: form.all_day ? null : form.end_time,
    recurring: form.recurring === RECURRING_NONE ? null : form.recurring
  });

  const handleSubmit = () => {
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    onSubmit(buildPayload());
  };

  // Phase D — trigger backend regen pipeline.
  // ElevenLabs-style: if the form has unsaved text edits, save them first so
  // the synthesized audio reflects what the user just typed. Single click =
  // one user-perceptible action. Mirrors FaqEditDialog.handleRegenerateAudio.
  const handleRegenerateAudio = async () => {
    if (!constraint?.id || !flowId || audioBusy) return;

    // Validate first — don't save invalid form just to enqueue a regen.
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) {
      setAudioError('請先修正欄位錯誤');
      return;
    }
    if (!form.message_text.trim()) {
      setAudioError('客戶聽到的回覆語音為空，無法生成');
      return;
    }

    setAudioError('');
    setAudioBusy(true);
    try {
      // If form is dirty, save first so the backend's regen reads the new text.
      // Phase A's beforeUpdate hook will flip ready→stale during this save,
      // which is fine — the next call (regenerateAudio) immediately overwrites
      // the status to 'pending'.
      if (isFormDirty()) {
        const saveResp = await voiceFlowConstraintService.update(
          flowId, constraint.id, buildPayload()
        );
        // Bubble the saved row up so the parent's table view + dirty check
        // baseline both reflect the new saved state.
        if (onRowSaved && saveResp?.data) {
          onRowSaved(saveResp.data);
        }
      }

      await voiceFlowConstraintService.regenerateAudio(flowId, constraint.id);
      // Optimistic flip — socket emit will land shortly and confirm.
      setAudioInfo((prev) => ({ ...prev, status: 'pending', error: null }));
    } catch (err) {
      setAudioBusy(false);
      setAudioError(err.message || '觸發語音生成失敗');
    }
  };

  // ----------------------------------------------------------------
  // 音檔面板（FAQ pattern — 狀態 chip + 重生按鈕 + audio player）
  // ----------------------------------------------------------------
  const renderAudioPanel = () => {
    // 新建模式：尚未儲存的限制不能生成語音
    if (!constraint?.id) {
      return (
        <Box sx={{
          mt: 0.5, p: 1.5,
          bgcolor: '#fafafa',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 1.5,
        }}>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            儲存後即可生成語音
          </Typography>
        </Box>
      );
    }

    const status = audioInfo.status || 'missing';
    const label = AUDIO_STATUS_LABELS[status] || '未產生';
    const inFlight = status === 'pending' || status === 'generating';
    const hasAudio = !!audioInfo.assetKey && (status === 'ready' || status === 'stale');
    // Cache-bust by audio_updated_at so a regen → fresh CloudFront fetch.
    const audioUrl = hasAudio && audioInfo.publicUrl
      ? `${audioInfo.publicUrl}?v=${audioInfo.updatedAt ? new Date(audioInfo.updatedAt).getTime() : audioInfo.duration || 0}`
      : null;
    const deployedNoPreview = hasAudio && !audioUrl;
    const buttonLabel = (status === 'ready' || status === 'stale')
      ? '重新生成語音'
      : '生成語音';

    return (
      <Box sx={{
        mt: 0.5, p: 1.5,
        bgcolor: '#fafafa',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', minWidth: 36 }}>
            語音
          </Typography>
          <Chip
            size="small"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {inFlight && <CircularProgress size={10} thickness={6} sx={{ color: 'rgba(0,0,0,0.55)' }} />}
                <span>{label}</span>
              </Box>
            }
            sx={NEUTRAL_CHIP_SX}
            title={audioInfo.error || (audioInfo.duration ? `時長 ${audioInfo.duration} 秒` : undefined)}
          />
          {audioInfo.duration && (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {audioInfo.duration} 秒
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            disabled={audioBusy || inFlight}
            onClick={handleRegenerateAudio}
            sx={{ fontSize: '12px', py: 0.25, px: 1, minHeight: 28 }}
          >
            {buttonLabel}
          </Button>
        </Box>
        {audioUrl && (
          <Box sx={{ mt: 1 }}>
            {/* key forces a fresh <audio> element after regen */}
            <audio
              key={audioUrl}
              src={audioUrl}
              controls
              preload="metadata"
              style={{ width: '100%', height: 32 }}
            />
          </Box>
        )}
        {deployedNoPreview && (
          <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneInTalkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              已部署於電話系統，無線上預覽
            </Typography>
          </Box>
        )}
        {audioError && (
          <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }}>
            {audioError}
          </FormHelperText>
        )}
        {status === 'failed' && audioInfo.error && (
          <FormHelperText error sx={{ mt: 0.5, fontSize: '12px' }} title={audioInfo.error}>
            上次生成失敗：{audioInfo.error.length > 60 ? `${audioInfo.error.slice(0, 60)}...` : audioInfo.error}
          </FormHelperText>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{constraint ? '編輯限制' : '新增限制'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="限制名稱"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || '管理者標籤，如「母親節」'}
              fullWidth
              size="small"
            />

            <TextField
              label="限制類型"
              select
              value={form.constraint_type}
              onChange={(e) => update('constraint_type', e.target.value)}
              fullWidth
              size="small"
            >
              {CONSTRAINT_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="起始日期"
                value={isoToDate(form.start_date)}
                onChange={(d) => update('start_date', dateToIso(d))}
                format="yyyy / MM / dd"
                slotProps={datePickerSlotProps(errors.start_date)}
              />
              <DatePicker
                label="結束日期"
                value={isoToDate(form.end_date)}
                onChange={(d) => update('end_date', dateToIso(d))}
                format="yyyy / MM / dd"
                slotProps={datePickerSlotProps(errors.end_date)}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={form.all_day}
                  onChange={(e) => update('all_day', e.target.checked)}
                />
              }
              label="整天"
            />

            {!form.all_day && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="時段啟始"
                  type="time"
                  value={form.start_time}
                  onChange={(e) => update('start_time', e.target.value)}
                  error={!!errors.start_time}
                  helperText={errors.start_time}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="時段結束"
                  type="time"
                  value={form.end_time}
                  onChange={(e) => update('end_time', e.target.value)}
                  error={!!errors.end_time}
                  helperText={errors.end_time}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            )}

            <TextField
              label="重複"
              select
              value={form.recurring}
              onChange={(e) => update('recurring', e.target.value)}
              fullWidth
              size="small"
              SelectProps={{
                renderValue: (v) => recurringLabel(v),
              }}
            >
              {RECURRING_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>

            <Box>
              <TextField
                label="客戶聽到的回覆語音"
                value={form.message_text}
                onChange={(e) => update('message_text', e.target.value)}
                error={!!errors.message_text}
                helperText={errors.message_text}
                multiline
                rows={3}
                fullWidth
                size="small"
              />
              {renderAudioPanel()}
            </Box>

            <TextField
              label="無法訂位後處理"
              select
              value={form.fallback_action}
              onChange={(e) => update('fallback_action', e.target.value)}
              fullWidth
              size="small"
            >
              {FALLBACK_ACTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="優先順序（數字越小越先）"
                type="number"
                value={form.priority}
                onChange={(e) => update('priority', e.target.value)}
                error={!!errors.priority}
                helperText={errors.priority}
                size="small"
                sx={{ width: 220 }}
                inputProps={{ min: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enabled}
                    onChange={(e) => update('enabled', e.target.checked)}
                  />
                }
                label="啟用"
              />
            </Box>

            {warnings && warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>後端提醒：</Typography>
                {warnings.map((w, idx) => (
                  <Typography key={idx} variant="caption" component="div">
                    • [{w.field}] {w.message}
                  </Typography>
                ))}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={saving}>取消</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? '儲存中…' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ConstraintEditDialog;
